import { CircularProgress } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import type { ButtonProps as MuiButtonProps } from "@mui/material";
import {
  dangerColor,
  dangerColor10,
  darkTextColor as textColor,
  secondaryTextColor,
  primaryColor,
  primaryColor10,
  textOnPrimary,
  // @ts-ignore
} from "../../scss/colors.module.scss";
// @ts-ignore
import { customFontSizeNormal } from "../../scss/fonts.module.scss";

type ButtonStyleParams = {
  customColor?: "danger" | "neutralPrimary" | "neutralSecondary";
  dense?: boolean;
  hidden?: boolean;
  variant?: MuiButtonProps["variant"];
};

const NEUTRAL_PRIMARY_COLOR = "var(--neutralPrimaryColor)";
const TEXT_ON_SECONDARY = "var(--textOnSecondary)";
const DARK_TEXT_COLOR = "var(--darkTextColor)";
const NEUTRAL_SECONDARY_BORDER = "1px solid var(--neutralSecondaryColor)";

function getButtonIconColor({
  customColor,
  variant,
}: Pick<ButtonStyleParams, "customColor" | "variant">): string {
  if (customColor === "danger") {
    return dangerColor;
  }
  if (customColor === "neutralPrimary") {
    return TEXT_ON_SECONDARY;
  }
  if (customColor === "neutralSecondary") {
    return DARK_TEXT_COLOR;
  }
  if (variant === "contained") {
    return textOnPrimary;
  }
  return primaryColor;
}

export const getStandardButtonSx = ({
  customColor,
  dense,
  hidden,
  variant,
}: ButtonStyleParams): SxProps<Theme> => ({
  padding: dense ? "4px 8px" : "8px 22px",
  display: "flex",
  alignItems: "center",
  fontSize: customFontSizeNormal,
  fontWeight: "bold",
  lineHeight: "20px",
  textTransform: "none",
  color: variant === "contained" && !customColor ? textOnPrimary : textColor,
  ...(variant === "contained" &&
    !customColor && {
      backgroundColor: primaryColor,
      "&:hover": {
        backgroundColor: primaryColor,
        filter: "brightness(0.95)",
      },
      "&.Mui-disabled": {
        color: secondaryTextColor,
        backgroundColor: primaryColor10,
      },
    }),
  width: "fit-content",
  borderColor: primaryColor,
  borderRadius: "4px",
  visibility: hidden ? "hidden" : "visible",
  ...(variant !== "contained" && {
    "&:hover": {
      backgroundColor: primaryColor10,
    },
  }),

  // Danger variant styles
  ...(customColor === "danger" && {
    borderColor: dangerColor,
    color: dangerColor,
    "&:hover": {
      borderColor: dangerColor,
      backgroundColor: dangerColor10,
    },
  }),

  // Neutral Primary variant styles (dark bg → light text)
  ...(customColor === "neutralPrimary" && {
    border: "none",
    backgroundColor: NEUTRAL_PRIMARY_COLOR,
    color: TEXT_ON_SECONDARY,
    "&:hover": {
      border: "none",
      backgroundColor: NEUTRAL_PRIMARY_COLOR,
      color: TEXT_ON_SECONDARY,
    },
    "&:disabled": {
      border: "none",
      backgroundColor: NEUTRAL_PRIMARY_COLOR,
      color: secondaryTextColor,
    },
  }),

  // Neutral Secondary variant styles
  ...(customColor === "neutralSecondary" && {
    backgroundColor: "transparent",
    border: NEUTRAL_SECONDARY_BORDER,
    color: DARK_TEXT_COLOR,
    "&:hover": {
      border: NEUTRAL_SECONDARY_BORDER,
      backgroundColor: "transparent",
      color: DARK_TEXT_COLOR,
    },
  }),

  "&:disabled": {
    color: secondaryTextColor,
  },

  "& .MuiButton-endIcon, & .MuiButton-startIcon": {
    color: getButtonIconColor({ customColor, variant }),
    "&.Mui-disabled": {
      color: secondaryTextColor,
    },
  },
});

export const CIRCULAR_PROGRESS_SX: SxProps<Theme> = {
  color: "black !important",
};

export { CircularProgress };
